import type { Command } from "../contracts";

// Compute a stable, deterministic signature for a command. Same (type, payload)
// → same signature. The pending registry uses this string as its key so:
//   - the same button on two list rows in flight runs independently;
//   - the same button on the same row dispatched twice coalesces (re-click is
//     a no-op while in-flight);
//   - workflow transitions and form-buttons live in different namespaces by
//     virtue of different commandType.
//
// Why not WeakMap-tagged custom signatures? Views never need to override —
// the payload IS the identity. If a future bulk-action surface needs batch
// grouping, it ships a dedicated platform command (`RUN_BULK_BUTTON`) whose
// signature includes the batchId. Easier to extend than to special-case.
export function commandSignature(command: Command): string {
    switch (command.type) {
        case "RUN_BUTTON": {
            const p = command.payload as {
                actionSlug: string;
                recordUuid: string;
                commandName: string;
                commandType?: string | null;
            };
            const engine = p.commandType || "default";
            return `RUN_BUTTON:${engine}:${p.actionSlug}:${p.commandName}:${p.recordUuid}`;
        }
        case "SAVE_RECORD":
            return "SAVE_RECORD";
        case "DELETE_RECORD": {
            const p = command.payload as { recordRefs: string[] };
            return `DELETE_RECORD:${p.recordRefs.slice().sort().join(",")}`;
        }
        case "RUN_REPORT": {
            const p = command.payload as { actionKey: string; recordRefs: string[] };
            return `RUN_REPORT:${p.actionKey}:${p.recordRefs.slice().sort().join(",")}`;
        }
        case "REFETCH_RECORD": {
            const p = command.payload as { modelKey?: string; recordUuid: string };
            return `REFETCH_RECORD:${p.modelKey || ""}:${p.recordUuid}`;
        }
        default:
            // Generic fallback — JSON.stringify is fine for navigation commands
            // since their payloads are tiny and key-stable.
            return `${command.type}:${stableStringify(command.payload)}`;
    }
}

// Sorts object keys so `{a:1,b:2}` and `{b:2,a:1}` produce the same string.
// Cheap because command payloads are shallow.
function stableStringify(value: unknown): string {
    if (value === null || value === undefined) return String(value);
    if (typeof value !== "object") return JSON.stringify(value);
    if (Array.isArray(value)) return `[${value.map(stableStringify).join(",")}]`;
    const obj = value as Record<string, unknown>;
    const keys = Object.keys(obj).sort();
    return `{${keys.map((k) => `${JSON.stringify(k)}:${stableStringify(obj[k])}`).join(",")}}`;
}
